import {Avatar, Box, Typography} from "@mui/material";
import React from "react";
import {RiskReportTypes} from "../../types";
import {api} from "../../http";
import {useGenericQuery} from "../../services/react-query/generic.query";

// "Status-tinted card" treatment (design artifact 77e1115b): the tile surface
// itself carries the finding — warm red tint on hit tiles, empty states stay
// white — with red otherwise reserved for meaning. Radii derive from the
// theme's 8px.
export const offenderTileSx = {
    p: 2.5,
    bgcolor: '#FFFBFA',
    border: '1px solid #F3D7D2',
    borderRadius: 1,
    boxShadow: '0 1px 2px rgba(183, 29, 24, 0.05)',
};

export const offenderChipSx = {
    bgcolor: '#DF3428',
    color: '#fff',
    fontWeight: 600,
    fontSize: '10.5px',
    letterSpacing: '0.05em',
    borderRadius: '6px',
    height: 22,
};

export const tileDividerSx = { borderColor: '#F3D7D2', my: 1.75 };

export const chargesBlockSx = { bgcolor: '#FBF1EF', borderRadius: '6px', px: 1.5, py: 1.25 };

// Registry photos come from Crimeometer via sex_offender_image_url; Cleara
// records carry an empty string, and stale URLs 404 — the Avatar children
// (initials) are the fallback for every one of those cases. Squared crop:
// registry photos are ID-style portraits.
// Registry hosts don't send CORS headers, so html2canvas dropped every photo
// from the PDF export (it re-fetches each <img> with crossOrigin="anonymous").
// Routing photos through the API's proxy fixes the export. <img> can't carry
// an Authorization header and the session JWT must not ride in URLs (access
// logs, history), so the API mints a short-lived photo-scoped token instead.
// The API signs it with a 5-minute TTL; refetching every 2 minutes means the
// token embedded in any rendered src always has 3+ minutes of validity left,
// and the shared query client hands that cached token to the detached PDF
// render. While the token loads, avatars show initials; if the endpoint is
// missing or failing (older API), fall back to the direct registry URL so
// the live view still shows photos and only the export degrades to initials.
// Response contract (pom-2-api getOffenderPhotoToken, PresenterFactory
// result): { token: string, expiresInSeconds: number }.
const PHOTO_TOKEN_REFETCH_MS = 2 * 60 * 1000;
const PHOTO_TOKEN_STALE_SECONDS = 2 * 60;

const usePhotoSrc = (rawUrl?: string): string | undefined => {
    const { data, isError } = useGenericQuery<{ token: string }>(
        'fetchOffenderPhotoToken',
        'reports/offender-photo-token',
        PHOTO_TOKEN_REFETCH_MS,
        undefined,
        true,
        PHOTO_TOKEN_STALE_SECONDS,
    );
    // https only: blocks javascript:/data: URIs and mixed content from a
    // compromised upstream record. Anything else falls back to initials.
    const url = rawUrl ?? '';
    if (!/^https:\/\//i.test(url)) return undefined;
    if (data?.token) {
        return `${api.defaults.baseURL}/reports/offender-photo?url=${encodeURIComponent(url)}&token=${encodeURIComponent(data.token)}`;
    }
    return isError ? url : undefined;
};

export const OffenderPhoto: React.FC<{ offender: RiskReportTypes.SexOffender; size?: number }> = ({ offender, size = 42 }) => {
    const initials = `${offender?.sex_offender_first_name?.charAt(0) ?? ''}${offender?.sex_offender_last_name?.charAt(0) ?? ''}` || '?';
    const photoUrl = usePhotoSrc(offender?.sex_offender_image_url);
    return (
        <Avatar
            variant="rounded"
            src={photoUrl}
            sx={{ bgcolor: '#ECEFF2', color: '#64707C', width: size, height: size }}
        >
            <Typography className={'text-download-adjust'} fontWeight={600} fontSize={Math.round(size * 0.3)}>
                {initials}
            </Typography>
        </Avatar>
    );
};

export const OffenderField: React.FC<{ label: string; value?: string | number | null; sx?: object }> = ({ label, value, sx }) => (
    <Box sx={sx}>
        <Typography sx={{ fontSize: '10.5px', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#919EAB' }}>
            {label}
        </Typography>
        <Typography
            className={'text-download-adjust'}
            sx={{ fontSize: '13px', color: '#212B36', fontVariantNumeric: 'tabular-nums', overflowWrap: 'break-word' }}
        >
            {value === 0 ? 0 : (value || 'N/A')}
        </Typography>
    </Box>
);

export const formatPhysical = (offender: RiskReportTypes.SexOffender): string => {
    const parts = [
        offender?.sex_offender_height,
        offender?.sex_offender_weight ? `${offender.sex_offender_weight}lbs` : '',
        offender?.sex_offender_hair ? `${offender.sex_offender_hair} hair` : '',
        offender?.sex_offender_eyes ? `${offender.sex_offender_eyes} eyes` : '',
    ].filter(Boolean);
    return parts.join(', ');
};

export const formatDemographics = (offender: RiskReportTypes.SexOffender): string => {
    const parts = [
        offender?.sex_offender_age ? `Age ${offender.sex_offender_age}` : '',
        offender?.sex_offender_sex,
        offender?.sex_offender_race,
    ].filter(Boolean);
    return parts.join(' · ');
};
